import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import translate from "../../services/translation.json";
import "./productLifecycle.scss";

const PHASE_ICONS = [
  // Research
  <svg key="i1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
    <circle cx="11" cy="11" r="7" />
    <path d="M20 20l-3.2-3.2" />
  </svg>,
  // Planning
  <svg key="i2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
    <rect x="5" y="3" width="14" height="18" rx="2" />
    <path d="M9 8h6M9 12h6M9 16h4" />
  </svg>,
  // Design
  <svg key="i3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
    <path d="M4 20L12 4l2.8 5.6L20 12l-8 8H4z" />
  </svg>,
  // Content
  <svg key="i4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
    <rect x="4" y="4" width="16" height="16" rx="2" />
    <path d="M8 9h8M8 13h8M8 17h5" />
  </svg>,
  // Development
  <svg key="i5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
    <rect x="3" y="3" width="7" height="7" />
    <rect x="14" y="3" width="7" height="7" />
    <rect x="3" y="14" width="7" height="7" />
    <rect x="14" y="14" width="7" height="7" />
  </svg>,
  // QA
  <svg key="i6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
    <path d="M5 12l4 4L19 6" />
  </svg>,
];

export default function ProductLifecycle({ lang }) {
  const copy = translate[lang].homepage;
  const phases = copy.plm_phases;
  const [activeIndex, setActiveIndex] = useState(0);
  const active = phases[activeIndex];
  const phaseNumber = String(activeIndex + 1).padStart(2, "0");

  const goPrev = () => {
    setActiveIndex((i) => (i === 0 ? phases.length - 1 : i - 1));
  };

  const goNext = () => {
    setActiveIndex((i) => (i === phases.length - 1 ? 0 : i + 1));
  };

  return (
    <section className="product-lifecycle" aria-labelledby="plm-title">
      <div className="plm-frame">
        <div className="plm-inner">
          <h2 id="plm-title" className="plm-title">
            {copy.product_lifecycle}
          </h2>
          <p className="plm-subtitle">{copy.product_lifecycle_subtitle}</p>

          <div className="plm-d-grid">
            <div className="plm-orbit" role="tablist" aria-label={copy.product_lifecycle}>
              <div className="plm-halo" aria-hidden="true" />
              <div className="plm-path" aria-hidden="true" />
              <div className="plm-core" aria-hidden="true">
                <strong>{copy.plm_core_label}</strong>
                <span>{copy.plm_core_sub}</span>
              </div>

              {phases.map((phase, index) => (
                <button
                  key={phase.title}
                  type="button"
                  role="tab"
                  aria-selected={index === activeIndex}
                  aria-label={`${phase.kicker}: ${phase.title}`}
                  className={`plm-node plm-node-${index + 1}${
                    index === activeIndex ? " active" : ""
                  }`}
                  onClick={() => setActiveIndex(index)}
                >
                  <span className="plm-badge">{index + 1}</span>
                  {PHASE_ICONS[index]}
                </button>
              ))}
            </div>

            <div className="plm-copy" role="tabpanel">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeIndex}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.25 }}
                >
                  <div className="plm-phase">
                    {copy.plm_phase_label} {phaseNumber} / 06 · {active.kicker}
                  </div>
                  <h3>{active.title}</h3>
                  <p className="plm-lead">{active.lead}</p>
                  <p className="plm-body">{active.body}</p>
                  <div className="plm-chips">
                    {active.chips.map((chip) => (
                      <span key={chip} className="plm-chip">
                        {chip}
                      </span>
                    ))}
                  </div>
                </motion.div>
              </AnimatePresence>

              <div className="plm-nav">
                <button
                  type="button"
                  onClick={goPrev}
                  aria-label={copy.plm_prev}
                >
                  ‹
                </button>
                <button
                  type="button"
                  onClick={goNext}
                  aria-label={copy.plm_next}
                >
                  ›
                </button>
                <span className="plm-hint">{copy.plm_nav_hint}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
